import { Helmet } from 'react-helmet';
import type { FunctionComponent } from 'react';
import type { InjectedRouter, RouterState } from 'react-router';
import type { Params } from 'react-router/lib/Router';

import DialogManager from '../components/DialogManager';
import { Link } from '../components/generic';
import FOSSAIconGrayscale from '../components/generic/FOSSAInlineSVG/FOSSAIconGrayscale';
import FOSSALogo from '../components/generic/FOSSAInlineSVG/FOSSALogo';
import Toaster from '../components/Toaster/Toaster';
import RouterContextProvider from '../contexts/RouterContext';
import COLORS from '../theme/colors';

import CurrentUserProvider from './contexts/CurrentUserContext';
import { Provider as IsPrivatePortalEnabledProvider } from './OrganizationPortal/hooks/useIsPrivatePortalEnabled';

import styles from './Application.module.less';

type Props = {
  router: InjectedRouter & { params: Params };
  location: RouterState['location'];
};

const learnMoreLink = 'https://fossa.com/product/sbom-management';
const createPortalLink = 'https://app.fossa.com/auth/register';
const fossaHomePageLink = 'https://fossa.com';

const Application: FunctionComponent<Props> = ({ router, location, children }) => {
  return (
    <RouterContextProvider router={router} location={location}>
      <CurrentUserProvider>
        <IsPrivatePortalEnabledProvider>
          <Helmet titleTemplate="%s | FOSSA" title="SBOM Portal" />
          <div className={styles.container}>
            <div className={styles.banner}>
              <div className={styles.branding}>
                <Link to={fossaHomePageLink}>
                  <div className={styles.branding}>
                    Powered by <FOSSAIconGrayscale /> <FOSSALogo width={53} color={COLORS.fossaGray60} />
                  </div>
                </Link>
              </div>
              <div className={styles.actions}>
                <Link to={learnMoreLink}>Learn more about SBOMs</Link> &bull;{' '}
                <Link to={createPortalLink}>Create your own SBOM portal</Link>
              </div>
            </div>
            <main className={styles.main}>{children}</main>
            <footer className={styles.footer}>
              <div>
                <FOSSAIconGrayscale width={33} /> <FOSSALogo width={53} color={COLORS.fossaGray60} />
              </div>
              <div>SBOM Portal created with FOSSA, Inc. Copyright &copy; {new Date().getFullYear()}</div>
              <div>
                <Link to={createPortalLink}>Get your own SBOM portal</Link> |{' '}
                <Link to={learnMoreLink}>Learn about SBOMs</Link>
              </div>
            </footer>
          </div>
          <DialogManager />
          <Toaster location={location} />
        </IsPrivatePortalEnabledProvider>
      </CurrentUserProvider>
    </RouterContextProvider>
  );
};

export default Application;
